// File: pages/AutenticationPages/LoginPage/index.jsx
import React, { useState } from "react";
import { Link, IconButton } from "@mui/material";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close"; 
import { useDispatch, useSelector } from "react-redux";
import AuthenticationLeftContent from "../../../components/common/AutenticationLeftContent";
import AuthenticationTextInput from "../../../components/common/TextInput/AuthenticationTextInput";
import Heading from "../../../components/common/AutenticationTitles/Heading";
import Subtitle from "../../../components/common/AutenticationTitles/Subtitle";
import PrimaryButton from "../../../components/common/PrimaryButton";
import { useNavigate, useLocation } from "react-router-dom";
import { loginUserAction, fetchUserDataAction } from "../../../store/slices/authSlice";

const LoginPage = ({ onClose, onViewChange, isMobile = false }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.auth);
  const theme = useTheme();
    const isLaptop = useMediaQuery(theme.breakpoints.down("lg"));
    const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 900px)");

  const validate = () => {
    const newErrors = {};
    if (!email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Invalid email";
    if (!password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      try {
        const result = await dispatch(loginUserAction({ email, password, rememberMe }));
        if (loginUserAction.fulfilled.match(result)) {
          // Fetch user profile data after successful login
          dispatch(fetchUserDataAction());

          // Return to the page the user was heading to before login (e.g. the 80G
          // form link from a receipt email, query params intact). RequireAuth puts
          // it in location.state.from; fall back to home when there isn't one.
          const from = location.state?.from;
          const returnTo = from ? `${from.pathname || ""}${from.search || ""}` : null;

          // Go to the page the user was heading to. Leaving /login unmounts the dialog, so
          // don't also call onClose (it navigates back / home and would race this).
          if (returnTo) {
            navigate(returnTo, { replace: true });
          } else if (onClose) {
            onClose();
          } else if (isMobile) {
            navigate("/");
          }
        }
      } catch (error) {
        console.error("Login failed:", error);
      }
    }
  };

  const handleRegisterClick = (e) => {
    e.preventDefault();
    if (onViewChange) {
      onViewChange("register");
    } else if (isMobile) {
      navigate("/register");
    }
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    if (onViewChange) {
      onViewChange("forgot");
    } else if (isMobile) {
      navigate("/forgot-password");
    }
  };

  return (
    <Box style={{ 
      display: "flex", 
      flexDirection: isMobile ? "column" : isTablet ? "column" : "row",
      backgroundColor: "#FFFFFF", 
      borderRadius: isMobile ? "12px" : isTablet ? "12px" : "22px",
      position: "relative", 
      padding: isMobile ? "0px" : isTablet ? "0px" : "20px 0px 20px 20px",
      height: isMobile ? "100vh" : isTablet ? "100vh" : "auto",
      overflow: isMobile ? "hidden" : isTablet ? "hidden" : "visible"
    }}>      
        <Box className="left-width" flex={1} sx={{maxWidth:isMobile ? "100%": isTablet ? "100%" : isLaptop? "300px" :"499px"}}>
          <AuthenticationLeftContent onClose={onClose} />
        </Box>
      <Box className="right-content" flex={1} p={isMobile ? 2 : isTablet ? 2 : 4} sx={{
        maxWidth:isMobile?"100%":isLaptop?"390px": "100%",
        marginInline: isMobile ? "0" : isTablet ? "0" : "auto",
        height: isMobile ? "50vh" : isTablet ? "60vh" : "auto",
        overflow: isMobile ? "auto" : isTablet ? "auto" : "visible",
        display: "flex",
        flexDirection: "column",
        justifyContent: isMobile ? "flex-start" : "center",
        width: isMobile ? "100%" : isTablet ? "100%" : "auto",
        position: "relative",
        borderRadius: isMobile ? "0 0 12px 12px" : isTablet ? "0 0 12px 12px" : "0"
      }}>
        <Heading>Login Your Account</Heading>
        <Subtitle>Enter your Login details below</Subtitle>
        <form onSubmit={handleSubmit}>
          <AuthenticationTextInput
            label="Email Address"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Enter Email Address"
            error={errors.email}
            helperText={errors.email}
            required
          />
          <Box sx={{position:"relative"}}>
             <Link 
              href="#" 
              underline="none" 
              onClick={handleForgotPasswordClick}
              style={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#1D1D1B",
                cursor: "pointer",
                position: "absolute",
                right:"0",
                top:"0",
                display: isMobile ? "block" : "none",
                zIndex: 1
              }}>
              Forgot Password?
            </Link>
          </Box>
          <AuthenticationTextInput
            label="Password"
            type="password"
            value={password}
            placeholder="Enter your Password"
            onChange={e => setPassword(e.target.value)}
            error={errors.password}
            helperText={errors.password}
            required
          />
          <Box  alignItems="center" justifyContent="space-between"
          sx={{marginBottom: isMobile ? "20px" : isTablet ? "20px" : "30px",display: isMobile ? "none" : "flex" }}>
            <label style={{ display: "flex", alignItems: "center", fontSize: "12px", fontWeight: 500, color:"#686866" }}>
              <input type="checkbox" style={{ marginRight: "8px", border:"1px solid #E6E6E6", borderRadius:"4px" }}
                     checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
              Remember Me
            </label>
            <Link 
              href="#" 
              underline="none" 
              onClick={handleForgotPasswordClick}
              style={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#1D1D1B",
                cursor: "pointer"
              }}>
              Forgot Password?
            </Link>
          </Box>
          <PrimaryButton disabled={loading}>
            {loading ? "Logging in..." : "Login"}
          </PrimaryButton>
        </form>
        {error && (
          <Typography 
            align="center" 
            mt={2} 
            style={{ 
              fontSize: "14px", 
              fontWeight: "500", 
              color: "#F04951" 
            }}
          >
            {error}
          </Typography>
        )}
        <Typography align="center" mt={3} style={{ fontSize: isMobile ? "14px" : isTablet ? "14px" : "16px", fontWeight:"500", color: "#4A4A49" }}>
          Don't have an account?{" "}
          <Link
            href="#"
            underline="none"
            onClick={e => {
              e.preventDefault();
              if (onViewChange) onViewChange("register");
            }}
            style={{ fontSize:  isMobile ? "14px" : isTablet ? "14px" : "16px", fontWeight:"700", color: "#141414", cursor: "pointer" }}
          >
            Register Now
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;